import { useCallback, useEffect, type RefObject } from 'react';

import {
  getDraggedFingering,
  createLetterElement,
  setDragHelperText,
  getDragHelperText,
  createLineElements,
  removeElementsByClassName,
} from '../utils/mainFingerEditUtils';

// Press-and-hold threshold (ms) used by the alt-fingering pointer
// handler to distinguish a "short tap → swap whole group" from a
// "long press → swap only the digit under the pointer".
const LONG_PRESS_MS = 250;

// Saved scroll position while a main-fingering drag locks the page.
// Module-scoped so nested lock/unlock calls don't lose the value.
let lockedScrollY = 0;

/**
 * Freeze the document so a touch-drag on an SVG fingering digit can't
 * scroll the page. `preventDefault` / `touch-action` on SVG `<text>`
 * is unreliable (especially on iOS); pinning `body` with
 * `position: fixed` is the approach that consistently works.
 */
const lockPageScroll = () => {
  lockedScrollY = window.scrollY;
  const { documentElement, body } = document;
  documentElement.style.overflow = 'hidden';
  body.style.overflow = 'hidden';
  body.style.position = 'fixed';
  body.style.top = `-${lockedScrollY}px`;
  body.style.left = '0';
  body.style.right = '0';
  body.style.width = '100%';
  body.style.touchAction = 'none';
};

const unlockPageScroll = () => {
  const { documentElement, body } = document;
  documentElement.style.overflow = '';
  body.style.overflow = '';
  body.style.position = '';
  body.style.top = '';
  body.style.left = '';
  body.style.right = '';
  body.style.width = '';
  body.style.touchAction = '';
  window.scrollTo(0, lockedScrollY);
};

export interface FingeringPointerTarget {
  noteId?: string;
  imageIndex: number;
  groupIndex: number;
  fingeringIndex: number;
  svgText: SVGTextElement;
}

export interface UseFingeringInteractionsParams {
  /**
   * Score stack that owns the overlay SVGs. Listeners are delegated
   * here so a ResizeObserver overlay rebuild cannot drop drag/swap
   * handlers.
   */
  rootRef: RefObject<HTMLElement | null>;
  /** Live-read flag so the handlers see edit mode without re-binding. */
  isEditModeRef: RefObject<boolean>;
  /** Drag finished on a main digit. `value` is the new digit string. */
  onEditMain: (target: FingeringPointerTarget, value: string) => void;
  /** `wide` is a short tap (contiguous run). A long press passes false. */
  onSwap: (
    target: Pick<
      FingeringPointerTarget,
      'noteId' | 'imageIndex' | 'groupIndex'
    >,
    wide: boolean
  ) => void;
}

const closestFingeringText = (
  event: Event,
  type: 'main' | 'alt'
): SVGTextElement | null => {
  const raw = event.target;
  const start =
    raw instanceof Element
      ? raw
      : raw instanceof Node
        ? raw.parentElement
        : null;
  return (
    start?.closest<SVGTextElement>(`text[data-fingering-type="${type}"]`) ??
    null
  );
};

const fingeringCoords = (text: SVGTextElement) => ({
  imageIndex: Number(text.dataset.imageIndex),
  groupIndex: Number(text.dataset.groupIndex),
  fingeringIndex: Number(text.dataset.fingeringIndex),
  noteId: text.dataset.noteId,
});

/**
 * Pointer and touch gestures for fingering digits. The hook does not
 * know whether the score is an engraved XML page or a PDF image page;
 * the caller applies the edit.
 */
export const useFingeringInteractions = ({
  rootRef,
  isEditModeRef,
  onEditMain,
  onSwap,
}: UseFingeringInteractionsParams) => {
  const handleDragStart = useCallback((event: Touch | MouseEvent) => {
    const xStart = event.pageX;
    const yStart = event.pageY;

    const target = event.target as HTMLElement;
    const originalFingering = target.textContent || '';

    const letterElement = createLetterElement(event, originalFingering);
    const numList = originalFingering.split('-').map(Number);
    const numLength = numList.length;
    createLineElements(event, numLength);
    return { xStart, yStart, letterElement, originalFingering };
  }, []);

  const handleDragMove = useCallback(
    (
      event: Touch | MouseEvent,
      xStart: number,
      yStart: number,
      letterElement: HTMLElement,
      originalFingering: string
    ) => {
      letterElement.style.top = `${event.pageY - 60}px`;
      letterElement.style.left = `${event.pageX}px`;
      setDragHelperText(
        letterElement,
        getDraggedFingering(
          event.pageX - xStart,
          event.pageY - yStart,
          originalFingering
        )
      );
    },
    []
  );

  const finishMainFingeringDrag = useCallback(
    (
      letterElement: HTMLElement,
      svgTextElement: SVGTextElement,
      imageIndex: number,
      groupIndex: number,
      fingeringIndex: number,
      noteId: string | undefined,
      unlockScroll: boolean
    ) => {
      const value = getDragHelperText(letterElement);
      onEditMain(
        {
          noteId,
          imageIndex,
          groupIndex,
          fingeringIndex,
          svgText: svgTextElement,
        },
        value
      );
      letterElement.remove();
      removeElementsByClassName('temp-line');
      if (unlockScroll) unlockPageScroll();
    },
    [onEditMain]
  );

  const handleMainFingeringTouchStart = useCallback(
    (
      event: TouchEvent,
      svgTextElement: SVGTextElement,
      imageIndex: number,
      groupIndex: number,
      fingeringIndex: number,
      noteId?: string
    ) => {
      if (!isEditModeRef.current) return;
      if (event.touches.length > 1) return;

      event.preventDefault();
      event.stopPropagation();

      lockPageScroll();

      const touch = event.changedTouches[0];
      const originalFingering = svgTextElement.textContent || '';
      const xStart = touch.clientX;
      const yStart = touch.clientY;

      const letterElement = createLetterElement(
        { pageX: touch.clientX, pageY: touch.clientY },
        originalFingering
      );
      letterElement.style.position = 'fixed';
      letterElement.style.left = `${touch.clientX}px`;
      letterElement.style.top = `${touch.clientY - 60}px`;
      letterElement.style.zIndex = '9999';

      const numList = originalFingering.split('-').map(Number);
      createLineElements(
        { pageX: touch.clientX, pageY: touch.clientY },
        numList.length
      );
      document.querySelectorAll<HTMLElement>('.temp-line').forEach((line) => {
        line.style.position = 'fixed';
      });

      const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault();
        if (e.touches.length > 1) {
          letterElement.remove();
          removeElementsByClassName('temp-line');
          unlockPageScroll();
          document.removeEventListener('touchmove', handleTouchMove);
          document.removeEventListener('touchend', handleTouchEnd);
          document.removeEventListener('touchcancel', handleTouchEnd);
          return;
        }
        const t = e.changedTouches[0];
        letterElement.style.top = `${t.clientY - 60}px`;
        letterElement.style.left = `${t.clientX}px`;
        setDragHelperText(
          letterElement,
          getDraggedFingering(
            t.clientX - xStart,
            t.clientY - yStart,
            originalFingering
          )
        );
      };

      const handleTouchEnd = () => {
        finishMainFingeringDrag(
          letterElement,
          svgTextElement,
          imageIndex,
          groupIndex,
          fingeringIndex,
          noteId,
          true
        );
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
        document.removeEventListener('touchcancel', handleTouchEnd);
      };

      document.addEventListener('touchmove', handleTouchMove, {
        passive: false,
      });
      document.addEventListener('touchend', handleTouchEnd);
      document.addEventListener('touchcancel', handleTouchEnd);
    },
    [finishMainFingeringDrag, isEditModeRef]
  );

  const handleMainFingeringPointerDown = useCallback(
    (
      event: PointerEvent,
      svgTextElement: SVGTextElement,
      imageIndex: number,
      groupIndex: number,
      fingeringIndex: number,
      noteId?: string
    ) => {
      if (!isEditModeRef.current) return;
      if (event.pointerType === 'touch') return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      event.preventDefault();
      event.stopPropagation();

      const { xStart, yStart, letterElement, originalFingering } =
        handleDragStart(event);

      const handlePointerMove = (e: PointerEvent) => {
        e.preventDefault();
        handleDragMove(e, xStart, yStart, letterElement, originalFingering);
      };

      const handlePointerEnd = () => {
        finishMainFingeringDrag(
          letterElement,
          svgTextElement,
          imageIndex,
          groupIndex,
          fingeringIndex,
          noteId,
          false
        );
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerEnd);
        document.removeEventListener('pointercancel', handlePointerEnd);
      };

      document.addEventListener('pointermove', handlePointerMove, {
        passive: false,
      });
      document.addEventListener('pointerup', handlePointerEnd);
      document.addEventListener('pointercancel', handlePointerEnd);
    },
    [handleDragStart, handleDragMove, finishMainFingeringDrag, isEditModeRef]
  );

  const handleAltFingeringPointerDown = useCallback(
    (
      event: PointerEvent,
      imageIndex: number,
      groupIndex: number,
      noteId?: string
    ) => {
      if (!isEditModeRef.current) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      let isLongPress = false;
      const holdTimer = setTimeout(() => {
        isLongPress = true;
        onSwap({ noteId, imageIndex, groupIndex }, false);
      }, LONG_PRESS_MS);

      const cancelHold = () => {
        clearTimeout(holdTimer);
        if (!isLongPress) {
          onSwap({ noteId, imageIndex, groupIndex }, true);
        }
        document.removeEventListener('pointerup', cancelHold);
        document.removeEventListener('pointercancel', cancelHold);
      };

      document.addEventListener('pointerup', cancelHold);
      document.addEventListener('pointercancel', cancelHold);
    },
    [onSwap, isEditModeRef]
  );

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const onPointerDown = (event: PointerEvent) => {
      const main = closestFingeringText(event, 'main');
      if (main) {
        const { imageIndex, groupIndex, fingeringIndex, noteId } =
          fingeringCoords(main);
        handleMainFingeringPointerDown(
          event,
          main,
          imageIndex,
          groupIndex,
          fingeringIndex,
          noteId
        );
        return;
      }
      const alt = closestFingeringText(event, 'alt');
      if (!alt) return;
      event.preventDefault();
      event.stopPropagation();
      const { imageIndex, groupIndex, noteId } = fingeringCoords(alt);
      handleAltFingeringPointerDown(event, imageIndex, groupIndex, noteId);
    };

    const onMainTouchStart = (event: TouchEvent) => {
      if (event.touches.length > 1) return;
      const text = closestFingeringText(event, 'main');
      if (!text) return;
      const { imageIndex, groupIndex, fingeringIndex, noteId } =
        fingeringCoords(text);
      handleMainFingeringTouchStart(
        event,
        text,
        imageIndex,
        groupIndex,
        fingeringIndex,
        noteId
      );
    };

    root.addEventListener('pointerdown', onPointerDown);
    root.addEventListener('touchstart', onMainTouchStart, { passive: false });

    return () => {
      root.removeEventListener('pointerdown', onPointerDown);
      root.removeEventListener('touchstart', onMainTouchStart);
    };
  }, [
    rootRef,
    handleMainFingeringPointerDown,
    handleMainFingeringTouchStart,
    handleAltFingeringPointerDown,
  ]);
};
